import React, { createContext, useContext, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useEscapeLayer } from '../../hooks/useEscapeLayer';

import styles from './Modal.module.css';

/** Lets `ModalTitle` take the id the dialog is labelled by, with no prop threading. */
const ModalTitleIdContext = createContext<string | undefined>(undefined);

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  /** Id of an element inside the modal that describes it (aria-describedby). */
  describedBy?: string;
}

function Modal({
  isOpen,
  onClose,
  children,
  className,
  describedBy,
}: ModalProps): React.ReactElement | null {
  const titleId = useId();
  const isDraggingRef = useRef(false);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  // Escape closes the modal only when it is the topmost overlay.
  useEscapeLayer(onClose, isOpen);
  useBodyScrollLock(isOpen);

  // Track mouse down to detect drag operations
  const handleMouseDown = (event: React.MouseEvent) => {
    mouseDownTargetRef.current = event.target;
    isDraggingRef.current = false;
  };

  // Track mouse move to detect dragging
  const handleMouseMove = () => {
    isDraggingRef.current = true;
  };

  // Handle click outside modal to close, but ignore drag operations
  const handleOverlayClick = (event: React.MouseEvent) => {
    // Only close if:
    // 1. The click target is the overlay (not a child element)
    // 2. The mouse down target was also the overlay (prevents drag from inside modal)
    // 3. This wasn't a drag operation (text selection)
    if (
      event.target === event.currentTarget &&
      mouseDownTargetRef.current === event.currentTarget &&
      !isDraggingRef.current
    ) {
      onClose();
    }

    // Reset drag tracking
    isDraggingRef.current = false;
    mouseDownTargetRef.current = null;
  };

  if (!isOpen) {
    return null;
  }

  // The overlay is presentational: a click on it closes the modal and Escape
  // is handled by the overlay stack (useEscapeLayer), so a select or
  // confirmation open inside the modal closes first. It used to be a
  // role="button", which made the whole dialog its descendant — and a button's
  // children are presentational, so assistive technology could flatten the
  // title, fields and actions out of the tree. The content is the dialog.
  return createPortal(
    <div
      className={styles.Overlay}
      onClick={handleOverlayClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onKeyDown={() => undefined}
      role="presentation"
    >
      <div
        className={`${styles.Modal} ${className || ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
      >
        <ModalTitleIdContext.Provider value={titleId}>{children}</ModalTitleIdContext.Provider>
      </div>
    </div>,
    document.body,
  );
}

function ModalHeader({ children }: { children: React.ReactNode }): React.ReactElement {
  return <div className={styles.ModalHeader}>{children}</div>;
}

function ModalTitle({ children }: { children: React.ReactNode }): React.ReactElement {
  const id = useContext(ModalTitleIdContext);
  return (
    <h2 id={id} className={styles.ModalTitle}>
      {children}
    </h2>
  );
}

function ModalContent({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return <div className={`${styles.ModalContent} ${className || ''}`}>{children}</div>;
}

function ModalBody({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return <div className={`${styles.ModalBody} ${className || ''}`}>{children}</div>;
}

function ModalFooter({ children }: { children: React.ReactNode }): React.ReactElement {
  return <div className={styles.ModalFooter}>{children}</div>;
}

function ModalCloseButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick?: () => void;
}): React.ReactElement {
  return (
    <button type="button" className={styles.ModalCloseButton} onClick={onClick}>
      {children}
    </button>
  );
}

export { Modal, ModalHeader, ModalTitle, ModalContent, ModalBody, ModalFooter, ModalCloseButton };
